/**
 * Regla de confianza sobre la instantánea de importes `importesEmision`, que el
 * servidor guarda en un documento al emitirlo. Es la ÚNICA implementación: la
 * usan el servidor (`functions/src/utils/totals.ts` la reexporta) y el cliente
 * (PDF y resúmenes), de modo que ambos deciden igual.
 *
 * Solo importa tipos de `./types` (se borran al compilar), así que puede importarse
 * desde Functions con ruta relativa sin arrastrar dependencias de ejecución.
 */
import {calcularImportes, centimosAEuros, redondearACentimos} from "./importes.js";
import type {Factura, ImportesEmision, InvoiceItem} from "./types.js";

/** Resultado de leer la instantánea de un documento (ver `evaluarInstantanea`). */
export type EstadoInstantanea =
  | {estado: "sin-instantanea"}
  | {estado: "valida"; importes: ImportesEmision}
  | {estado: "mal-formada"};

const esNumeroFinito = (valor: unknown): valor is number =>
  typeof valor === "number" && Number.isFinite(valor);

/**
 * ¿Tiene la forma de `ImportesEmision` versión 1? Comprueba solo la forma (tipos
 * de los campos, no que las cifras cuadren: eso lo hacen `totalLiquidoCuadra` y
 * `lineasCuadranConInstantanea`). Es la única definición de «instantánea bien
 * formada»: el dueño puede escribir el campo directamente en Firestore, así que
 * nada de lo que se lea de él puede dar por hecho su forma.
 */
export function esInstantaneaBienFormada(valor: unknown): valor is ImportesEmision {
  if (typeof valor !== "object" || valor === null) return false;
  const i = valor as Record<string, unknown>;
  return i.version === 1 &&
    Array.isArray(i.tiposIva) && i.tiposIva.every(esNumeroFinito) &&
    Array.isArray(i.desglose) && i.desglose.every((grupo) =>
      typeof grupo === "object" && grupo !== null &&
      esNumeroFinito((grupo as Record<string, unknown>).tipoIva) &&
      esNumeroFinito((grupo as Record<string, unknown>).base) &&
      esNumeroFinito((grupo as Record<string, unknown>).cuota)) &&
    esNumeroFinito(i.irpfRate) && esNumeroFinito(i.baseImponible) &&
    esNumeroFinito(i.cuotaTotal) && esNumeroFinito(i.irpf) && esNumeroFinito(i.importeTotal);
}

/**
 * Lee la instantánea de importes de un documento. Es el único sitio donde se
 * decide si se confía en ella:
 *
 * - Documento NO emitido (sin `verifactu`): `sin-instantanea`. Un borrador nunca
 *   tiene `verifactu`, así que la huella es lo único que distingue un documento
 *   emitido; el `estado` NO cuenta (el dueño puede escribirlo a mano en Firestore
 *   y un `Borrador` con huella no puede degradar el documento a la vía antigua).
 * - Emitido sin `importesEmision` (undefined o null; documentos anteriores a este
 *   campo): `sin-instantanea`, camino antiguo.
 * - Emitido con `importesEmision` bien formada: `valida`.
 * - Emitido con `importesEmision` que no es una instantánea v1 bien formada (forma
 *   rota o versión desconocida): `mal-formada`. NO se degrada al camino antiguo:
 *   eso dejaría que una instantánea corrompida esquivase las comprobaciones más
 *   estrictas. Quien la lee debe tratar el documento como inválido (validación) o
 *   negarse a generar importes (XML).
 */
export function evaluarInstantanea(
  documento: {
    verifactu?: Factura["verifactu"];
    importesEmision?: unknown;
  }
): EstadoInstantanea {
  if (!documento.verifactu) return {estado: "sin-instantanea"};
  const importes = documento.importesEmision;
  if (importes === undefined || importes === null) return {estado: "sin-instantanea"};
  return esInstantaneaBienFormada(importes) ? {estado: "valida", importes} : {estado: "mal-formada"};
}

/**
 * Líquido a pagar de una instantánea: ImporteTotal − retención, en euros
 * derivados de céntimos enteros (la resta de dos decimales en coma flotante
 * podría arrastrar ruido).
 */
export function totalLiquidoDeEmision(importes: ImportesEmision): number {
  return centimosAEuros(redondearACentimos(importes.importeTotal) - redondearACentimos(importes.irpf));
}

/**
 * ¿`total == importeTotal − irpf`, en céntimos enteros? Es la relación con la que
 * se guardó el líquido al emitir. Se exige siempre, sin excepciones.
 */
export function totalLiquidoCuadra(
  documento: Pick<Factura, "total">,
  importes: ImportesEmision
): boolean {
  return redondearACentimos(documento.total) === redondearACentimos(totalLiquidoDeEmision(importes));
}

/**
 * ¿Las líneas del documento reproducen la instantánea? Recalcula los importes con
 * `items` (cantidad y precio), el tipo de IVA guardado por línea (`tiposIva`) y la
 * tasa de IRPF guardada, y compara en céntimos enteros base, desglose, cuota total,
 * IRPF e importe total. No lee la configuración actual (impuestos o perfil): detecta
 * cualquier edición de las líneas tras sellar sin invalidar un documento genuino
 * porque luego cambien los impuestos.
 */
export function lineasCuadranConInstantanea(
  items: InvoiceItem[],
  importes: ImportesEmision
): boolean {
  if (importes.tiposIva.length !== items.length) return false;
  const lineas = items.map((item, i) => ({
    cantidad: item.cantidad,
    precioUnitario: item.precioUnitario,
    rate: importes.tiposIva[i],
  }));
  const calculado = calcularImportes(lineas, importes.irpfRate);
  if (calculado.grupos.length !== importes.desglose.length) return false;
  const gruposIguales = calculado.grupos.every((grupo, i) => {
    const guardado = importes.desglose[i];
    return grupo.rate === guardado.tipoIva &&
      grupo.baseCentimos === redondearACentimos(guardado.base) &&
      grupo.cuotaCentimos === redondearACentimos(guardado.cuota);
  });
  return gruposIguales &&
    calculado.baseTotalCentimos === redondearACentimos(importes.baseImponible) &&
    calculado.cuotaTotalCentimos === redondearACentimos(importes.cuotaTotal) &&
    calculado.irpfCentimos === redondearACentimos(importes.irpf) &&
    calculado.baseTotalCentimos + calculado.cuotaTotalCentimos === redondearACentimos(importes.importeTotal);
}

/**
 * Regla única de «se puede usar la instantánea para mostrar o generar»: las líneas
 * la reproducen Y el líquido guardado es `importeTotal − irpf`. Es lo que aplican el
 * cliente (PDF y resúmenes) y el servidor (Facturae, cadena, validador; este último
 * y la cadena llaman a las dos comprobaciones por separado solo para informar de
 * cuál falla). Nunca lanza: un dato corrupto (cantidad o total no numéricos) no
 * cuadra, no rompe el render ni la generación.
 */
export function instantaneaCoherente(
  documento: {items: InvoiceItem[]; total: number},
  importes: ImportesEmision
): boolean {
  try {
    return lineasCuadranConInstantanea(documento.items, importes) &&
      totalLiquidoCuadra(documento, importes);
  } catch {
    return false;
  }
}
